// Corrections found after a statement was released.
//
// The figures above this block are what the owner was sent and they never move.
// This block is what has since been found to be true, why, and where the money
// was settled. Three facts that do not contradict each other, which is the whole
// point: a statement can be true without being reopened.
//
// It renders on issued statements only, because a draft has nothing to correct
// yet: a draft regenerates and the figure simply changes.
import { useEffect } from 'react'
import { ArrowRight, CircleAlert, CircleCheck } from 'lucide-react'
import { useGetStatementWrites } from '../../hooks/backend/statements'
import { Spinner } from '../../lib/shadcn/spinner'
import { formatZar } from '../../utils/statements'
import { cn } from '../../lib/shadcn/utils'

export type StatementWrite = {
  id: string
  statement_id: string
  plate: string
  period_label: string
  line_code: string | null
  label: string
  as_issued_zar: number
  true_zar: number
  delta_zar: number
  reason: string
  settled_period_start: string | null
  settlement_status: string
}

export function StatementWrites({ statementId }: { statementId: string }) {
  const query = useGetStatementWrites<StatementWrite[]>()
  const { trigger } = query

  useEffect(() => {
    if (!statementId) return
    trigger({ statement_id: statementId }).catch(() => {})
  }, [trigger, statementId])

  if (query.loading) {
    return (
      <div className="flex items-center gap-2 px-4 py-3 text-sm text-muted-foreground">
        <Spinner className="h-4 w-4" />
        Checking for corrections since this statement went out
      </div>
    )
  }

  // A read that failed must never look like "nothing was corrected". Those are
  // opposite claims and they render identically if you are careless.
  if (query.error) {
    return (
      <div className="px-4 py-3 text-sm text-muted-foreground">
        Could not check for corrections just now. The figures above are
        unaffected. This is not the same as there being none.
      </div>
    )
  }

  const writes = query.data ?? []
  if (writes.length === 0) return null

  const net = writes.reduce((sum, w) => sum + w.delta_zar, 0)
  const unsettled = writes
    .filter((w) => !w.settled_period_start)
    .reduce((sum, w) => sum + w.delta_zar, 0)

  return (
    <div className="border-t border-border">
      <div className="flex flex-wrap items-baseline justify-between gap-2 bg-muted/40 px-4 py-2">
        <div>
          <span className="text-sm font-semibold">Corrections since this statement went out</span>
          <p className="mt-0.5 max-w-2xl text-xs text-muted-foreground">
            The figures above are what you were sent and they have not been
            changed. These are the things we have since found, and where the
            difference was settled.
          </p>
        </div>
        <span
          className={cn(
            'tabular-nums text-sm font-semibold',
            net < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400',
          )}
        >
          {formatZar(net)}
        </span>
      </div>

      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-[11px] uppercase tracking-wide text-muted-foreground">
            <th className="px-4 py-1.5 text-left font-semibold">Line</th>
            <th className="px-2 py-1.5 text-right font-semibold">As sent</th>
            <th className="px-2 py-1.5 text-right font-semibold">Should have read</th>
            <th className="px-2 py-1.5 text-right font-semibold">Difference</th>
            <th className="px-4 py-1.5 text-left font-semibold">Settled</th>
          </tr>
        </thead>
        <tbody>
          {writes.map((w) => (
            <tr key={w.id} className="border-b border-border/60 align-top">
              <td className="px-4 py-2">
                <div className="font-medium">{w.label}</div>
                <p className="mt-0.5 max-w-xl text-xs text-muted-foreground">{w.reason}</p>
              </td>
              <td className="px-2 py-2 text-right tabular-nums text-muted-foreground">
                {formatZar(w.as_issued_zar)}
              </td>
              <td className="px-2 py-2 text-right tabular-nums">{formatZar(w.true_zar)}</td>
              <td
                className={cn(
                  'px-2 py-2 text-right tabular-nums font-medium',
                  w.delta_zar < 0
                    ? 'text-rose-600 dark:text-rose-400'
                    : 'text-emerald-600 dark:text-emerald-400',
                )}
              >
                {formatZar(w.delta_zar)}
              </td>
              <td className="px-4 py-2">
                <span
                  className={cn(
                    'inline-flex items-center gap-1 text-xs',
                    w.settled_period_start
                      ? 'text-muted-foreground'
                      : 'font-medium text-amber-600 dark:text-amber-400',
                  )}
                >
                  {w.settled_period_start ? (
                    <CircleCheck className="h-3.5 w-3.5 shrink-0" />
                  ) : (
                    <CircleAlert className="h-3.5 w-3.5 shrink-0" />
                  )}
                  {w.settlement_status}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Unsettled is the figure that matters operationally: money recognised as
          owed with nowhere to land, almost always because the vehicle has left
          the fleet. It is stated rather than buried. */}
      {unsettled !== 0 && (
        <div className="flex items-center gap-2 border-t border-border bg-amber-50 px-4 py-2 text-xs text-amber-800 dark:bg-amber-950 dark:text-amber-300">
          <ArrowRight className="h-3.5 w-3.5 shrink-0" />
          <span>
            <strong className="tabular-nums">{formatZar(unsettled)}</strong> of this has been
            recognised but not settled, because there is no open statement on this vehicle to carry
            it.
          </span>
        </div>
      )}
    </div>
  )
}
